/**
 * Instacut plan entitlements — SINGLE source of truth.
 * Every UI gate (barber limit, analytics window, visit history, etc.)
 * MUST come from here. Don't sprinkle plan checks across the codebase.
 */

// Price of one additional barber slot beyond the plan's included cap.
// Premium: base £49/mo covers 3 barbers, each extra is £8/mo.
export const EXTRA_BARBER_PRICE_GBP = 8;

// Hard ceiling on total barbers (base + extras) so the UI doesn't grow
// unbounded. Extras beyond this need product intervention.
export const MAX_BARBERS_WITH_EXTRAS = 10;

export const PLAN_FEATURES = {
  standard: {
    includedBarbers: 1,
    extrasAllowed: false,           // Standard cannot buy extra slots
    sms: false,
    reviews: false,
    waitlist: false,
    rebooking: false,
    visitHistory: false,
    analyticsMaxDays: 30,
  },
  premium: {
    includedBarbers: 3,
    extrasAllowed: true,            // £8/mo per extra
    sms: true,
    reviews: true,
    waitlist: true,
    rebooking: true,
    visitHistory: true,
    analyticsMaxDays: 90,
  },
};

/**
 * Resolve features for a given shop. Accepts a shop row OR a plan string.
 * Falls back to standard if anything is missing.
 *
 * Backwards-compat: exposes `maxBarbers` = `includedBarbers + (extras || 0)`
 * so existing call sites (Barbers page nudge, Analytics gate, etc.) keep
 * working without modification.
 */
export const featuresFor = (shopOrPlan) => {
  const isObj  = shopOrPlan && typeof shopOrPlan === 'object';
  const plan   = (isObj ? shopOrPlan?.plan : shopOrPlan) || 'standard';
  const extras = isObj ? Number(shopOrPlan?.extra_barbers || 0) : 0;
  const key    = (plan || '').toLowerCase();
  const base   = PLAN_FEATURES[key] || PLAN_FEATURES.standard;
  return {
    ...base,
    plan: key,
    extras,
    maxBarbers: base.includedBarbers + (base.extrasAllowed ? extras : 0),
  };
};

/**
 * Barber-capacity helper used by the Barbers page + onboarding wizards.
 * Given a shop and its current barber count, returns:
 *   - `underCap`    : count is within included + extras (add is free)
 *   - `atCap`       : count == included + extras (next add needs an extra)
 *   - `overCap`     : count > included + extras (edge case; UI should disable)
 *   - `canBuyExtra` : plan allows paid extras and hasn't hit MAX_BARBERS_WITH_EXTRAS
 *   - `nextExtraCost` : how much the *next* extra costs per month
 *
 * `overrides` (optional): `{ includedBarbers, extraBarberPrice }` sourced
 * from `usePricing()` so the live editable values from platform_settings
 * take precedence over the hard-coded defaults on this file.
 */
export const barberCapacity = (shopOrPlan, currentCount, overrides = {}) => {
  const f = featuresFor(shopOrPlan);
  const included = Number.isFinite(overrides.includedBarbers)
    ? overrides.includedBarbers
    : f.includedBarbers;
  const extras   = f.extras;
  const cap      = included + extras;
  const cost     = Number.isFinite(overrides.extraBarberPrice)
    ? overrides.extraBarberPrice
    : EXTRA_BARBER_PRICE_GBP;
  return {
    plan: f.plan,
    included,
    extras,
    cap,
    underCap: currentCount <  cap,
    atCap:    currentCount === cap,
    overCap:  currentCount >  cap,
    canBuyExtra: f.extrasAllowed && (cap < MAX_BARBERS_WITH_EXTRAS),
    nextExtraCost: cost,
  };
};

/**
 * Hook-friendly: derive entitlements from the AppShell profile.
 */
import { useShell } from '../components/AppShell';
export const useEntitlements = () => {
  const { profile } = useShell();
  const shop = profile?.shops || null;
  const features = featuresFor(shop);
  const isActive = ['active', 'trialing'].includes(shop?.subscription_status || 'active');
  return { plan: features.plan, features, isActive, shop };
};
